import { Controller } from "@hotwired/stimulus"

/**
 * JAP1 QCM Controller
 * Handles the QCM (multiple choice quiz) interface
 */
export default class extends Controller {
  static targets = [
    "modeSelection",
    "quizArea",
    "results",
    "timer",
    "currentQuestion",
    "totalQuestions",
    "progressBar",
    "questionLabel",
    "questionText",
    "options",
    "optionA",
    "optionB",
    "optionC",
    "optionD",
    "optionTextA",
    "optionTextB",
    "optionTextC",
    "optionTextD",
    "feedback",
    "feedbackBox",
    "feedbackIcon",
    "feedbackTitle",
    "feedbackText",
    "nextBtn",
    "resultIcon",
    "scoreCorrect",
    "scorePercent",
    "scoreDuration"
  ]

  static values = {
    mode: { type: String, default: "" },
    chapitre: Number,
    questions: { type: Array, default: [] },
    currentIndex: { type: Number, default: 0 },
    score: { type: Number, default: 0 },
    answered: { type: Boolean, default: false }
  }

  connect() {
    this.startTime = null
    this.timerInterval = null
    this.answers = []

    // If mode is pre-selected via URL, start immediately
    if (this.modeValue) {
      this.startMode({ currentTarget: { dataset: { mode: this.modeValue } } })
    }
  }

  disconnect() {
    if (this.timerInterval) {
      clearInterval(this.timerInterval)
    }
  }

  // Start quiz with selected mode
  startMode(event) {
    const mode = event.currentTarget.dataset.mode
    this.modeValue = mode

    // Generate questions based on mode
    this.generateQuestions(mode)

    // Hide mode selection, show quiz area
    if (this.hasModeSelectionTarget) {
      this.modeSelectionTarget.classList.add("hidden")
    }
    if (this.hasQuizAreaTarget) {
      this.quizAreaTarget.classList.remove("hidden")
    }

    // Start timer
    this.startTimer()

    // Display first question
    this.displayQuestion()
  }

  // Generate questions based on mode
  generateQuestions(mode) {
    // For now, use placeholder questions
    // In production, this would load from JSON data
    const allQuestions = this.getQuestionPool()

    let count = 15
    if (mode === "marathon") count = 50
    if (mode === "chapitre") count = 10

    // Shuffle and take required number
    const shuffled = this.shuffleArray([...allQuestions])
    this.questionsValue = shuffled.slice(0, Math.min(count, shuffled.length))

    if (this.hasTotalQuestionsTarget) {
      this.totalQuestionsTarget.textContent = this.questionsValue.length
    }
  }

  // Get pool of questions (placeholder - would load from JSON)
  getQuestionPool() {
    // Placeholder questions for testing
    return [
      {
        question: "Quelles sont les dimensions d'un terrain de padel ?",
        correct: "20m x 10m",
        options: ["20m x 10m", "18m x 9m", "22m x 11m", "24m x 12m"]
      },
      {
        question: "Quelle est la hauteur du filet au centre ?",
        correct: "88 cm",
        options: ["85 cm", "88 cm", "92 cm", "95 cm"]
      },
      {
        question: "Quelle est la hauteur du filet aux extrémités ?",
        correct: "92 cm",
        options: ["88 cm", "90 cm", "92 cm", "95 cm"]
      },
      {
        question: "Combien de temps a le serveur pour servir après l'annonce du score ?",
        correct: "25 secondes",
        options: ["20 secondes", "25 secondes", "30 secondes", "15 secondes"]
      },
      {
        question: "Quelle est la durée maximale d'un temps mort médical ?",
        correct: "3 minutes",
        options: ["2 minutes", "3 minutes", "5 minutes", "10 minutes"]
      },
      {
        question: "À quel score y a-t-il changement de côté ?",
        correct: "Quand la somme des jeux est impaire",
        options: ["Tous les 2 jeux", "Quand la somme des jeux est impaire", "Tous les 3 jeux", "À chaque set"]
      },
      {
        question: "Que se passe-t-il si la balle touche le filet et retombe dans le bon carré de service ?",
        correct: "Let - on rejoue",
        options: ["Faute", "Let - on rejoue", "Point pour le serveur", "Point pour le relanceur"]
      },
      {
        question: "Un joueur peut-il sortir du terrain pour jouer la balle ?",
        correct: "Oui, par les portes ou par-dessus les parois",
        options: ["Non, jamais", "Oui, uniquement par les portes", "Oui, par les portes ou par-dessus les parois", "Oui, mais seulement en double"]
      },
      {
        question: "Quelle est la pression d'une balle de padel homologuée ?",
        correct: "Entre 4,6 et 5,2 kg",
        options: ["Entre 3,5 et 4,5 kg", "Entre 4,6 et 5,2 kg", "Entre 5,5 et 6,5 kg", "Entre 6 et 7 kg"]
      },
      {
        question: "Quelle est la longueur maximale d'une raquette de padel ?",
        correct: "45,5 cm",
        options: ["40 cm", "45,5 cm", "50 cm", "55 cm"]
      },
      {
        question: "Le service doit-il être effectué en diagonale ?",
        correct: "Oui, toujours",
        options: ["Non", "Oui, toujours", "Seulement le premier service", "Seulement en double"]
      },
      {
        question: "Combien de rebonds la balle peut-elle faire avant d'être jouée ?",
        correct: "Un seul rebond maximum",
        options: ["Aucun", "Un seul rebond maximum", "Deux rebonds", "Autant que souhaité"]
      },
      {
        question: "Quelle est la hauteur des parois latérales ?",
        correct: "3 mètres de grillage + mur",
        options: ["2 mètres", "3 mètres de grillage + mur", "4 mètres", "Variable selon le terrain"]
      },
      {
        question: "Peut-on jouer la balle après qu'elle ait touché le grillage ?",
        correct: "Oui, si elle n'a fait qu'un rebond au sol",
        options: ["Non, jamais", "Oui, toujours", "Oui, si elle n'a fait qu'un rebond au sol", "Seulement sur son propre terrain"]
      },
      {
        question: "Quelle est la distance entre la ligne de service et le filet ?",
        correct: "6,95 mètres",
        options: ["5 mètres", "6 mètres", "6,95 mètres", "7,5 mètres"]
      }
    ]
  }

  // Display current question
  displayQuestion() {
    if (this.currentIndexValue >= this.questionsValue.length) {
      this.showResults()
      return
    }

    const q = this.questionsValue[this.currentIndexValue]
    this.answeredValue = false

    // Update question label
    if (this.hasQuestionLabelTarget) {
      this.questionLabelTarget.textContent = `Question ${this.currentIndexValue + 1}`
    }

    // Update current question number
    if (this.hasCurrentQuestionTarget) {
      this.currentQuestionTarget.textContent = this.currentIndexValue + 1
    }

    // Update question text
    if (this.hasQuestionTextTarget) {
      this.questionTextTarget.textContent = q.question
    }

    // Shuffle and display options
    const shuffledOptions = this.shuffleArray([...q.options])
    const letters = ["A", "B", "C", "D"]

    letters.forEach((letter, index) => {
      const optionBtn = this[`hasOption${letter}Target`] ? this[`option${letter}Target`] : null
      const optionText = this[`hasOptionText${letter}Target`] ? this[`optionText${letter}Target`] : null

      if (optionBtn && optionText) {
        optionText.textContent = shuffledOptions[index] || ""
        optionBtn.dataset.answer = shuffledOptions[index]
        optionBtn.disabled = false

        // Reset styles
        optionBtn.classList.remove("border-green-500", "bg-green-50", "border-red-500", "bg-red-50")
        optionBtn.classList.add("border-gray-200")
      }
    })

    // Update progress bar
    if (this.hasProgressBarTarget) {
      const percent = ((this.currentIndexValue + 1) / this.questionsValue.length) * 100
      this.progressBarTarget.style.width = `${percent}%`
    }

    // Hide feedback and next button
    if (this.hasFeedbackTarget) {
      this.feedbackTarget.classList.add("hidden")
    }
    if (this.hasNextBtnTarget) {
      this.nextBtnTarget.classList.add("hidden")
    }
  }

  // Handle option selection
  selectOption(event) {
    if (this.answeredValue) return

    this.answeredValue = true
    const selectedAnswer = event.currentTarget.dataset.answer
    const correctAnswer = this.questionsValue[this.currentIndexValue].correct
    const isCorrect = selectedAnswer === correctAnswer

    // Update score
    if (isCorrect) {
      this.scoreValue++
    }

    // Store answer
    this.answers.push({
      questionIndex: this.currentIndexValue,
      selected: selectedAnswer,
      correct: correctAnswer,
      isCorrect
    })

    // Disable all options
    const letters = ["A", "B", "C", "D"]
    letters.forEach(letter => {
      const btn = this[`hasOption${letter}Target`] ? this[`option${letter}Target`] : null
      if (btn) {
        btn.disabled = true

        // Highlight correct/incorrect
        if (btn.dataset.answer === correctAnswer) {
          btn.classList.remove("border-gray-200")
          btn.classList.add("border-green-500", "bg-green-50")
        } else if (btn === event.currentTarget && !isCorrect) {
          btn.classList.remove("border-gray-200")
          btn.classList.add("border-red-500", "bg-red-50")
        }
      }
    })

    // Show feedback
    this.showFeedback(isCorrect, correctAnswer)

    // Show next button
    if (this.hasNextBtnTarget) {
      this.nextBtnTarget.classList.remove("hidden")
    }
  }

  // Show feedback after answering
  showFeedback(isCorrect, correctAnswer) {
    if (!this.hasFeedbackTarget) return

    this.feedbackTarget.classList.remove("hidden")

    if (this.hasFeedbackBoxTarget) {
      this.feedbackBoxTarget.classList.remove("bg-green-50", "bg-red-50", "border-green-200", "border-red-200")
      this.feedbackBoxTarget.classList.add(
        isCorrect ? "bg-green-50" : "bg-red-50",
        isCorrect ? "border-green-200" : "border-red-200",
        "border"
      )
    }

    if (this.hasFeedbackIconTarget) {
      this.feedbackIconTarget.innerHTML = isCorrect
        ? '<svg class="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path></svg>'
        : '<svg class="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path></svg>'
    }

    if (this.hasFeedbackTitleTarget) {
      this.feedbackTitleTarget.textContent = isCorrect ? "Bonne réponse !" : "Mauvaise réponse"
      this.feedbackTitleTarget.classList.remove("text-green-800", "text-red-800")
      this.feedbackTitleTarget.classList.add(isCorrect ? "text-green-800" : "text-red-800")
    }

    if (this.hasFeedbackTextTarget) {
      this.feedbackTextTarget.textContent = isCorrect
        ? "Continue comme ça !"
        : `La bonne réponse était : ${correctAnswer}`
      this.feedbackTextTarget.classList.remove("text-green-700", "text-red-700")
      this.feedbackTextTarget.classList.add(isCorrect ? "text-green-700" : "text-red-700")
    }
  }

  // Go to next question
  nextQuestion() {
    this.currentIndexValue++
    this.displayQuestion()
  }

  // Start the timer
  startTimer() {
    this.startTime = Date.now()
    this.timerInterval = setInterval(() => this.updateTimer(), 1000)
  }

  // Update timer display
  updateTimer() {
    if (!this.hasTimerTarget) return

    const elapsed = Math.floor((Date.now() - this.startTime) / 1000)
    const minutes = Math.floor(elapsed / 60)
    const seconds = elapsed % 60
    this.timerTarget.textContent = `${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`
  }

  // Show final results
  showResults() {
    // Stop timer
    if (this.timerInterval) {
      clearInterval(this.timerInterval)
    }

    // Hide quiz area, show results
    if (this.hasQuizAreaTarget) {
      this.quizAreaTarget.classList.add("hidden")
    }
    if (this.hasResultsTarget) {
      this.resultsTarget.classList.remove("hidden")
    }

    // Calculate stats
    const correct = this.scoreValue
    const total = this.questionsValue.length
    const percent = Math.round((correct / total) * 100)
    const elapsed = Math.floor((Date.now() - this.startTime) / 1000)
    const minutes = Math.floor(elapsed / 60)
    const seconds = elapsed % 60

    // Update result display
    if (this.hasScoreCorrectTarget) {
      this.scoreCorrectTarget.textContent = `${correct}/${total}`
    }
    if (this.hasScorePercentTarget) {
      this.scorePercentTarget.textContent = `${percent}%`
    }
    if (this.hasScoreDurationTarget) {
      this.scoreDurationTarget.textContent = `${minutes}:${seconds.toString().padStart(2, "0")}`
    }

    // Update result icon based on score
    if (this.hasResultIconTarget) {
      if (percent >= 80) {
        this.resultIconTarget.classList.remove("bg-yellow-100", "bg-red-100")
        this.resultIconTarget.classList.add("bg-green-100")
        this.resultIconTarget.innerHTML = '<svg class="w-10 h-10 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>'
      } else if (percent >= 50) {
        this.resultIconTarget.classList.remove("bg-green-100", "bg-red-100")
        this.resultIconTarget.classList.add("bg-yellow-100")
        this.resultIconTarget.innerHTML = '<svg class="w-10 h-10 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"></path></svg>'
      } else {
        this.resultIconTarget.classList.remove("bg-green-100", "bg-yellow-100")
        this.resultIconTarget.classList.add("bg-red-100")
        this.resultIconTarget.innerHTML = '<svg class="w-10 h-10 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>'
      }
    }

    // Save result to localStorage
    this.saveResult(correct, total, elapsed)
  }

  // Save quiz result to localStorage
  saveResult(correct, total, duration) {
    const progressController = this.getProgressController()
    if (progressController) {
      progressController.addExamResult("qcm", { correct, total }, duration)
    }
  }

  // Get progress controller
  getProgressController() {
    const element = document.querySelector("[data-controller~='jap1-progress']")
    if (element) {
      return this.application.getControllerForElementAndIdentifier(element, "jap1-progress")
    }
    return null
  }

  // Restart quiz
  restart() {
    this.currentIndexValue = 0
    this.scoreValue = 0
    this.answeredValue = false
    this.answers = []

    // Hide results, show mode selection
    if (this.hasResultsTarget) {
      this.resultsTarget.classList.add("hidden")
    }
    if (this.hasModeSelectionTarget) {
      this.modeSelectionTarget.classList.remove("hidden")
    }

    // Reset timer display
    if (this.hasTimerTarget) {
      this.timerTarget.textContent = "00:00"
    }
  }

  // Utility: shuffle array
  shuffleArray(array) {
    for (let i = array.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [array[i], array[j]] = [array[j], array[i]]
    }
    return array
  }
}
